import useNswagClient from './../../hooks/api/useNswagClient';
import { UserContext } from './../../components/shared/useUser';
import { theme } from './../../theme';
import useLogError from './../../hooks/useLogError';
import { ChangeEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Franchise, Store } from './../../app/services/api/generated';
import { compare } from './../../utils';
import { PodConfigurationModel, podClient } from './../../app/services/api/podClient';
import PageArea from './../../components/shared/PageArea';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, FormControl, Grid, InputAdornment, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import PageTitle from './../../components/shared/PageTitle';
import LoadingWheel from './../../components/ui/LoadingWheel';
import { ConfigurationLevel } from './enums/ConfigurationLevel';
import { ExpandMore } from '@mui/icons-material';
import CustomPagination from './../../components/forms/Pagination';
import { mergeArrays } from './helpers/ArrayHelper';
import StoreConfigurationsList from './components/StoreConfigurationsList';
import MessagesSnackbar from './../../components/shared/MessagesSnackbar';
import { SearchSm } from './../../assets';
import FranchiseConfigurationsList from './components/FranchiseConfigurationsList';
import { Item, ItemFilterParams, client } from './../../app/services/api/orderManagementClient';
import PodConfigurationInfo from './components/modals/PodConfigurationInfo';
import CustomSwitch from './../../components/shared/CustomSwitch';

const ManagePodConfigurations = () => {
  const { t } = useTranslation('managePodConfigurations');
  const { user, isAdmin, hasPermissionTo, selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [search, setSearch] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [allFranchises, setAllFranchises] = useState<Franchise[]>([]);
  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [allConfigurations, setAllConfigurations] = useState<PodConfigurationModel[]>([]);
  const [existingConfigurations, setExistingConfigurations] = useState<PodConfigurationModel[]>([]);
  const [existingStoreConfigurations, setExistingStoreConfigurations] = useState<PodConfigurationModel[]>([]);
  const [availableConfigs, setAvailableConfigs] = useState<PodConfigurationModel[]>([]);
  const [pickedStore, setPickedStore] = useState<Store | null>(null);
  const [configurationLevel, setConfigurationLevel] = useState<ConfigurationLevel | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<PodConfigurationModel | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isPodInfoModalOpen, setIsPodInfoModalOpen] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [franchiseStoreConfigurations, setFranchiseStoreConfigurations] = useState<PodConfigurationModel[]>([]);

  const { getFranchises, getFranchiseStores } = useNswagClient();

  const isStoreManager = () => hasPermissionTo(['AIOrderingStore']) && !hasPermissionTo(['AIOrderingAdmin']);
  const isAIOrderingAdmin = () => hasPermissionTo(['AIOrderingAdmin']);
  
  const isStoreEnabledForPredictiveOrdering = () => {
    // Check if the current store has predictive ordering enabled in franchise store configurations
    if (pickedStore && franchiseStoreConfigurations.length > 0) {
      const storeConfig = franchiseStoreConfigurations.find(
        config => config.storeNumber === pickedStore.storeNumber && 
                 config.itemNumber === '', // Store-level configuration has no item number
      );
      return storeConfig?.enabled === true;
    }
    return false;
  };

  // UI state helpers for role-based field access control
  const areTogglesReadOnly = () => isStoreManager();
  const areMinMaxFieldsReadOnly = () => isAIOrderingAdmin();
  const isOnlyDigits = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return true;
    return /^\d+$/.test(String(value));
  };
  const shouldDisableSaveButton = (config: PodConfigurationModel) => {
    const errorKey = `${config.franchiseName}-${config.storeNumber}-${config.itemNumber}`;
    const hasStoredError = !!validationErrors[errorKey];
    const hasCurrentValidationError = !!getValidationError(config);
    // Only allow save if all numeric fields are blank or only digits
    const allNumericValid =
      isOnlyDigits(config.minStockOnHandQuantity) &&
      isOnlyDigits(config.maxStockOnHandQuantity);
    return hasStoredError || hasCurrentValidationError || !allNumericValid;
  };

  useEffect(() => {
    loadFranchises();
  }, []);

  useEffect(() => {
    loadStores();
  }, [selectedFranchise]);

  useEffect(() => {
    getConfigurationLevel();
    setPage(0);
  }, [selectedFranchise, pickedStore]);

  useEffect(() => {
    setAvailableConfigs(getAvailableConfigsFromItems(configurationLevel));
  }, [configurationLevel, allFranchises, allStores, allItems]);
  
  useEffect(() => {
    loadItems();
  }, [selectedFranchise, pickedStore]);

  useEffect(() => {
    // Load franchise store configurations whenever we have a franchise
    // This is needed for isStoreEnabledForPredictiveOrdering() to work properly
    // even when we're at store level
    if (selectedFranchise) {
      loadFranchiseStoreConfigurations();
    }
  }, [selectedFranchise]);

  useEffect(() => {
    const configs = mergeArrays(availableConfigs, existingConfigurations);
    const sortedConfigs = (configs ?? []).sort((a, b) => {
      return Number(b.enabled) - Number(a.enabled);
    });
    setAllConfigurations(sortedConfigs);
  }, [availableConfigs, existingConfigurations]);
  const getConfigurationLevel = () => {
    let configLevel = ConfigurationLevel.Global;

    if (selectedFranchise) {
      configLevel = ConfigurationLevel.Franchise;
    }

    if (pickedStore) {
      configLevel = ConfigurationLevel.Store;
    }

    setConfigurationLevel(configLevel);
    
    // Load configurations based on current stable state
    if (configLevel === ConfigurationLevel.Global && !selectedFranchise) {
      loadConfigurations(ConfigurationLevel.Global);
    } else if (configLevel === ConfigurationLevel.Franchise && selectedFranchise) {
      loadConfigurations(ConfigurationLevel.Franchise);
    } else if (configLevel === ConfigurationLevel.Store && selectedFranchise && pickedStore) {
      loadConfigurations(ConfigurationLevel.Store);
    }
  };

  const loadFranchises = () => {
    if (!isAdmin()) {
      const userFranchise: Franchise = {
        franchiseName: user?.franchiseName ?? '',
      };
      setAllFranchises([userFranchise]);
      setSelectedFranchise(userFranchise);
      return;
    }
    setIsLoading(true);
    getFranchises().then(result => {
      if (result.data) {
        setAllFranchises(result.data.sort((a, b) => compare(a, b, 'franchiseName', 'asc')));
        if (result.data.length === 1) {
          setSelectedFranchise(result.data[0]);
        }
      }
    }).catch((error) => {
      logError(error); 
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const loadStores = () => {
    setAllStores([]);
    setPickedStore(null);

    if (!selectedFranchise?.franchiseName) return;

    if (isAdmin()) {
      getFranchiseStores(selectedFranchise?.franchiseName)
        .then((result) => {
          if (result.data?.stores) {
            setAllStores(result.data?.stores.filter(x => x.starbucksStoreNumber).sort((a, b) => compare(a, b, 'storeName', 'asc')));
          }
        }).catch((error) => {
          logError(error); 
        });
    }
    else {
      const userStores = user?.stores ?? [];
      setAllStores(userStores);
      const currentStoreNumber = selectedStore?.storeNumber;
      const currentStore = userStores.find(s => s.storeNumber === currentStoreNumber);
      if(currentStore) {
        setPickedStore(currentStore);
      } else if (userStores.length > 0) {
        setPickedStore(userStores[0]);
      }
    }
  };

  const loadItems = () => {
    setAllItems([]);
    if (!selectedFranchise) return;

    setIsLoading(true);

    // Load all items at once. avoids pagination issues with sorting by enabled status
    const itemParams: ItemFilterParams = {
      marketName: selectedFranchise?.franchiseName ?? '',
      storeNumber: pickedStore?.storeNumber ?? '',
      search: '', // doing client-side filtering
      pageNumber: 0,
      pageSize: 10000, // get all items
    };
    client.getItemsByFranchiseName(itemParams).then((result) => {
      if (result.data) {
        const uniqueConfigurations = result.data.items
          .reduce((acc: Item[], config) => {
            if (!acc.some(item => item.sku === config.sku)) {
              acc.push(config);
            }
            return acc;
          }, []);
          // Store all items for sorting after configuration merge
        setAllItems(uniqueConfigurations);
      }
    }).catch((error) => {
      logError(error); 
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const loadConfigurations = (configLevel: ConfigurationLevel | null) => {
    setAllConfigurations([]);
    setSelectedConfig(null);
    setSelectedIndex(-1);
    
    if (!configLevel) return;

    podClient.getConfigurations(configLevel, selectedFranchise?.franchiseName, pickedStore?.storeNumber)
      .then(res => {
        if (configLevel == ConfigurationLevel.Franchise) {
          setExistingConfigurations(res.filter(x => x.itemNumber));
          setExistingStoreConfigurations(res.filter(x => !x.itemNumber));
          return;
        }
        setExistingConfigurations(res);
      }).catch((error) => {
        logError(error);
      });
  };

  const loadFranchiseStoreConfigurations = () => {
    setFranchiseStoreConfigurations([]);
    
    if (!selectedFranchise?.franchiseName) return;

    // Load franchise-level store configurations (configurationLevel = 1 for Franchise)
    podClient.getConfigurations(ConfigurationLevel.Franchise, selectedFranchise.franchiseName, '')
      .then(res => {
        // Filter to get only store configurations (no itemNumber)
        const storeConfigs = res.filter(x => !x.itemNumber);
        setFranchiseStoreConfigurations(storeConfigs);
      }).catch((error) => {
        logError(error);
      });
  };

  const getAvailableConfigsFromItems = (configurationLevel: ConfigurationLevel | null) => {
    return allItems.map((x) => {
      const config: PodConfigurationModel = {
        id: 0,
        franchiseName: selectedFranchise?.franchiseName ?? '',
        itemNumber: x.gtin && x.gtin.trim() !== '' ? x.gtin : x.sku ?? '', // TODO: OM119ish issue. Consider replacing with 'usesSkuMapping' logic when available
        itemDescription: x.name ?? '',
        configurationLevel: configurationLevel ?? ConfigurationLevel.Store,
        minStockOnHandQuantity: null,
        maxStockOnHandQuantity: null,
        safetyStock: null, // Safety stock is now automatically calculated by the backend
        isParEnabled: false,
        enabled: false,
        storeNumber: '',
        storeName: '',
        starbucksStoreNumber: '',
        franchiseStores: [],
      };

      if (configurationLevel == ConfigurationLevel.Store) {
        config.storeNumber = pickedStore?.storeNumber ?? '';
        config.storeName = pickedStore?.storeName ?? '';
        config.starbucksStoreNumber = pickedStore?.starbucksStoreNumber ?? '';
      }

      return config;
    });
  };

  const handleFranchiseChange = (event: SyntheticEvent, value: Franchise | null) => {
    setSelectedFranchise(value);
  };

  const handleStoreChange = (event: SyntheticEvent, value: Store | null) => {
    setPickedStore(value);
  };
  const handleConfigChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const { name, value } = event.target;
    const actualIndex = getActualIndex(index);

    const minValue = 0;
    
    const newValue = Number(value);

    const isValidNumber = /^\d*$/.test(value);

    if (value !== '' && (!isValidNumber || newValue < minValue)) {
      return;
    }

    let hasIndexChanged = false;
    const updatedItems = [...allConfigurations];

    if (selectedConfig && selectedIndex !== actualIndex) {
      updatedItems[selectedIndex] = { ...selectedConfig };
      hasIndexChanged = true;
    }

    if (hasIndexChanged || !selectedConfig) {
      setSelectedConfig(allConfigurations[actualIndex]);
    }

    updatedItems[actualIndex] = { 
      ...updatedItems[actualIndex], 
      [name]: value === '' ? '' : newValue.toString(),
    };

    setAllConfigurations(updatedItems);
    setSelectedIndex(actualIndex);
  };
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, checked } = event.target;
    const actualIndex = getActualIndex(index);

    let hasIndexChanged = false;
    const updatedItems = [...allConfigurations];

    if (selectedConfig && selectedIndex !== actualIndex) {
      updatedItems[selectedIndex] = { ...selectedConfig };
      hasIndexChanged = true;
    }

    if (hasIndexChanged || !selectedConfig) {
      setSelectedConfig(allConfigurations[actualIndex]);
    }

    updatedItems[actualIndex] = { 
      ...updatedItems[actualIndex], 
      [name]: checked,
    };

    setAllConfigurations(updatedItems);
    setSelectedIndex(actualIndex);
  };
  const handleCancel = (index: number) => {
    if (!selectedConfig) {
      return;
    }

    const actualIndex = getActualIndex(index);
    const updatedItems = [...allConfigurations];
    updatedItems[actualIndex] = { ...selectedConfig };

    setAllConfigurations(updatedItems);
    setSelectedConfig(null);
    setSelectedIndex(-1);
  };

  const handlePageChange = (pageNumber: number) => {
    handleCancel(selectedIndex);
    setPage(pageNumber);
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;

    handleCancel(selectedIndex);
    setSearch(newSearchValue);
    setPage(0); // Reset to first page when search changes
  };
    // Validation functions
  const validateMinMaxStock = (config: PodConfigurationModel): string | null => {
    // Default min to 0 if not configured
    const min = config.minStockOnHandQuantity ? Number(config.minStockOnHandQuantity) : 0;
    const max = Number(config.maxStockOnHandQuantity);

    // Skip validation if both fields are empty/null
    if (!config.minStockOnHandQuantity && !config.maxStockOnHandQuantity) {
      return null;
    }

    // Get pack size for validation
    const item = allItems.find(x => x.sku === config.itemNumber);
    const packSize = item?.packSize ? Number(item.packSize) : null;

    // Validate max >= min when both provided
    if (config.minStockOnHandQuantity && config.maxStockOnHandQuantity && max < min) {
      return t('maxStockMustBeHigherThanMin');
    }
    
    // Validate maxStockOnHandQuantity >= packSize + min (min defaults to 0)
    if (config.maxStockOnHandQuantity && packSize && !isNaN(packSize)) {
      const minimumRequired = packSize + min;
      if (max < minimumRequired) {
        return t('maxStockMustBeAtLeastConversionPlusMin', { value: minimumRequired });
      }
    }

    return null;
  };

  const getValidationError = (config: PodConfigurationModel): string | null => {
    if (configurationLevel === ConfigurationLevel.Store) {
      return validateMinMaxStock(config);
    }
    return null;
  };


  const handleSave = (item: PodConfigurationModel) => {
    // Validate before saving
    const validationError = getValidationError(item);
    if (validationError) {
      const errorKey = `${item.franchiseName}-${item.storeNumber}-${item.itemNumber}`;
      setValidationErrors({ ...validationErrors, [errorKey]: validationError });
      return;
    }

    // Clear any existing validation errors for this item
    const errorKey = `${item.franchiseName}-${item.storeNumber}-${item.itemNumber}`;
    const newErrors = { ...validationErrors };
    delete newErrors[errorKey];
    setValidationErrors(newErrors);

    setIsLoading(true);
    const isCurrentlyEnabled = selectedConfig?.enabled;
    setSelectedConfig(null);
    setSelectedIndex(-1);

    item.minStockOnHandQuantity = item.minStockOnHandQuantity === '' ? null : item.minStockOnHandQuantity;
    item.maxStockOnHandQuantity = item.maxStockOnHandQuantity === '' ? null : item.maxStockOnHandQuantity;

    if (item.configurationLevel === ConfigurationLevel.Franchise && item.itemNumber) {
      item.storeName = '';
      item.storeNumber = '';
      item.starbucksStoreNumber = '';
    }

    if (item.id > 0) {
      podClient.update(item)
        .then(() => {
          loadConfigurations(configurationLevel);
          if (!isCurrentlyEnabled && item.enabled && item.itemNumber) setIsPodInfoModalOpen(true);
          setShowSuccessMessage(true);
        }).catch((error) => {
          logError(error);
          setShowErrorMessage(true);
        }).finally(() => {
          setIsLoading(false);
        });
      return;
    }

    podClient.create(item)
      .then(() => {
        loadConfigurations(configurationLevel);
        if (item.enabled && item.itemNumber) setIsPodInfoModalOpen(true);
        setShowSuccessMessage(true);
      }).catch((error) => {
        logError(error);
        setShowErrorMessage(true);
      }).finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = () => {
    // No need to call API - filtering is handled by getVisibleConfigurations()
    setPage(0);
  };

  const handleClear = () => {
    setSearch('');
    setPage(0);
  };
  const hasPendingChanges = (config: PodConfigurationModel) => JSON.stringify(selectedConfig) !== JSON.stringify(config);

  const getItemUom = (itemNumber: string) => {
    const item = allItems.find(x => x.sku === itemNumber);
    
    const primaryUom = item?.primaryUom;
    const orderableUom = item?.orderableUom;
    return primaryUom ?? orderableUom ?? 'EA';
  };

  // Helper function to get paginated configurations for display
  const getVisibleConfigurations = () => {
    // Filter configurations based on search term
    const filteredConfigs = allConfigurations.filter(config => 
      search === '' || config.itemNumber.toLowerCase().includes(search.toLowerCase()) ||
      config.itemDescription.toLowerCase().includes(search.toLowerCase()),
    );
    
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredConfigs.slice(startIndex, endIndex);
  };

  // Helper function to get max pages based on total configurations
  const getMaxPages = () => {
    const filteredCount = allConfigurations.filter(config => 
      search === '' || config.itemNumber.toLowerCase().includes(search.toLowerCase()) ||
      config.itemDescription.toLowerCase().includes(search.toLowerCase()),
    ).length;
    return Math.ceil(filteredCount / rowsPerPage);
  };

  // Helper function to convert visible index to actual array index
  const getActualIndex = (visibleIndex: number) => {    // Get the filtered configurations
    const filteredConfigs = allConfigurations.filter(config => 
      search === '' || config.itemNumber.toLowerCase().includes(search.toLowerCase()) ||
      config.itemDescription.toLowerCase().includes(search.toLowerCase()),
    );
    
    // Get the configuration at the visible index
    const actualIndex = page * rowsPerPage + visibleIndex;
    const targetConfig = filteredConfigs[actualIndex];
    
    // Find the actual index in the full configurations array
    return allConfigurations.findIndex(config => 
      config.itemNumber === targetConfig?.itemNumber &&
      config.franchiseName === targetConfig?.franchiseName &&
      config.storeNumber === targetConfig?.storeNumber,
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingWheel />;
    }

    if (configurationLevel === ConfigurationLevel.Global) {
      return (
        <FranchiseConfigurationsList franchises={allFranchises}
          existingFranchiseConfigurations={existingConfigurations}
          handleSave={handleSave}></FranchiseConfigurationsList>
      );
    }

    if (configurationLevel == ConfigurationLevel.Franchise) {
      return (
        <>
          <Accordion sx={{ mb: '25px' }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
            >
              <Box sx={{ display: 'flex', p: '8px' }}>
                <Typography sx={{ pl: '5px' }}>
                  {t('storeConfigurations')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {
                selectedFranchise &&
                          <StoreConfigurationsList franchiseName={selectedFranchise?.franchiseName ?? ''}
                            stores={allStores}
                            existingStoreConfigurations={existingStoreConfigurations}
                            handleSave={handleSave}></StoreConfigurationsList>
              }
            </AccordionDetails>
          </Accordion>
          {renderData()}
        </>
      );
    }

    if (allConfigurations.length == 0) {
      return (
        <Typography variant="textMD"
          sx={{ display: 'flex', flex: 1, justifyContent: 'left' }}>{t('noData')}
        </Typography>
      );
    }
    
    return renderData();
  };
  const renderData = () => {
    // Only show min/max stock on hand columns for item+store level (configurationLevel === ConfigurationLevel.Store)
    const showMinMaxStockColumns = configurationLevel === ConfigurationLevel.Store;

    // Check if store is enabled for Predictive Ordering
    if (configurationLevel === ConfigurationLevel.Store && !isStoreEnabledForPredictiveOrdering()) {
      return (
        <Typography variant="textMD"
          sx={{ display: 'flex', flex: 1, justifyContent: 'center', p: 4 }}>
          {t('predictiveOrderingNotEnabledForStore')}
        </Typography>
      );
    }

    return (
      <>
        <Grid container
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={2}
          mb={5}>
          <Grid item
            xs={12}
            md={8}>
            <FormControl fullWidth
              sx={{ p: '10px' }}>
              <TextField placeholder={t('search')}
                size='small'
                autoComplete='off'
                id="search-input"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon><SearchSm/></SvgIcon>
                    </InputAdornment>
                  ),
                }}
                value={search}
                onChange={handleSearchChange}
                fullWidth
              />
            </FormControl>
          </Grid>
          <Grid item
            xs={12}
            md={2}>
            <Button
              fullWidth
              variant="primary"
              size="lg"
              onClick={() => handleSearch()}>
              {t('search')}
            </Button>
          </Grid>
          <Grid item
            xs={12}
            md={2}>
            <Button
              fullWidth
              variant="secondary"
              size="lg"
              onClick={() => handleClear()}>
              {t('clear')}
            </Button>
          </Grid>
        </Grid>
        <TableContainer sx={{ overflowY:'auto' }}>
          <Table sx={{ tableLayout: 'fixed', minWidth: 900, overflow: 'hidden' }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('itemNumber')}</TableCell>
                <TableCell>{t('description')}</TableCell>
                <TableCell sx={{ textAlign: 'center', width: 120 }}>{t('predictiveOrdering')}</TableCell>
                {configurationLevel === ConfigurationLevel.Store && (
                  <TableCell sx={{ textAlign: 'center', width: 120 }}>{t('parEnabled')}</TableCell>
                )}
                {showMinMaxStockColumns && <TableCell sx={{ textAlign: 'center', width: 140 }}>{t('minStockOnHandQuantity')}</TableCell>}
                {showMinMaxStockColumns && <TableCell sx={{ textAlign: 'center', width: 140 }}>{t('maxStockOnHandQuantity')}</TableCell>}
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getVisibleConfigurations().map((row, index) => (
                <TableRow
                  key={`${row.franchiseName}-${row.storeNumber}-${row.itemNumber}`}>
                  <TableCell>{row.itemNumber}</TableCell>
                  <TableCell
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.itemDescription}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <CustomSwitch checked={row.enabled}
                      name='enabled'
                      disabled={areTogglesReadOnly()}
                      onChange={($event) => handleCheckboxChange($event, index)} />
                  </TableCell>
                  {
                    configurationLevel === ConfigurationLevel.Store &&
                    <TableCell sx={{ textAlign: 'center' }}>
                      <CustomSwitch checked={row.isParEnabled}
                        name='isParEnabled'
                        disabled={!row.enabled || areTogglesReadOnly()}
                        onChange={($event) => handleCheckboxChange($event, index)} />
                    </TableCell>
                  }
                  {showMinMaxStockColumns && (
                    <TableCell sx={{ textAlign: 'center', width: 140 }}>
                      <TextField
                        type="number"
                        name="minStockOnHandQuantity"
                        value={row.minStockOnHandQuantity ?? ''}
                        disabled={!row.enabled || areMinMaxFieldsReadOnly()}
                        onChange={($event) => handleConfigChange($event, index)}
                        size="small"
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          min: 0,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {getItemUom(row.itemNumber)}
                            </InputAdornment>
                          ),
                          sx: areMinMaxFieldsReadOnly() ? {
                            backgroundColor: theme.palette.custom.gray[100],
                            '& .MuiInputBase-input': {
                              color: theme.palette.custom.gray[400],
                            },
                          } : {},
                        }}
                      />
                    </TableCell>
                  )}

                  {showMinMaxStockColumns && (
                    <TableCell sx={{ textAlign: 'center', width: 140 }}>
                      <TextField
                        type="number"
                        name="maxStockOnHandQuantity"
                        value={row.maxStockOnHandQuantity ?? ''}
                        disabled={!row.enabled || areMinMaxFieldsReadOnly()}
                        onChange={($event) => handleConfigChange($event, index)}
                        size="small"
                        error={!!getValidationError(row)}
                        helperText={getValidationError(row)}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          min: 0,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {getItemUom(row.itemNumber)}
                            </InputAdornment>
                          ),
                          sx: areMinMaxFieldsReadOnly() ? {
                            backgroundColor: theme.palette.custom.gray[100],
                            '& .MuiInputBase-input': {
                              color: theme.palette.custom.gray[400],
                            },
                          } : {},
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ textAlign: 'center' }}>
                    {
                      selectedIndex === getActualIndex(index) && hasPendingChanges(row) &&
                      <>
                        <Button
                          variant="primary"
                          sx={{ m: 1 }}
                          size="md"
                          disabled={shouldDisableSaveButton(row)}
                          onClick={() => handleSave(row)}>
                          {t('save')}
                        </Button>
                        <Button
                          variant="secondary"
                          sx={{ m: 1 }}
                          size="md"
                          onClick={() => handleCancel(index)}>
                          {t('cancel')}
                        </Button>
                      </>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ minWidth: 900 }}>
            <CustomPagination
              page={page}
              setPage={handlePageChange}
              maxPages={getMaxPages()}
              breakpointForChangeDisplay={120000}
            ></CustomPagination>
          </Box>
        </TableContainer>
      </>
    );
  };

  return (
    <PageArea>
      <Box
        sx={{
          textAlign: 'left',
          color: 'black',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          alignSelf: 'stretch',
        }}>
        <PageTitle>{' ' + t('title')}</PageTitle>
      </Box>
      <Grid container
        sx={{ mb: '12px' }}
        alignItems={'center'}
        spacing={5}>
        <Grid item
          xs={12}
          md={6}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              options={allFranchises}
              value={selectedFranchise}
              onChange={handleFranchiseChange}
              getOptionKey={(option) => option.franchiseName ?? ''}
              getOptionLabel={(option) => option.franchiseName ?? ''}
              disabled={allFranchises.length <= 1}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('franchise')}
                  placeholder={t('selectFranchise')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              options={allStores}
              value={pickedStore}
              onChange={handleStoreChange}
              getOptionKey={(option) => option.storeNumber ?? ''}
              getOptionLabel={(option) => option.storeName ?? ''}
              disableClearable={isStoreManager()}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('store')}
                  placeholder={t('selectStore')} />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={{
        display: 'flex', flexDirection: 'column', width: '100%', height: '100%', p: '12px', border: `solid 1px ${theme.palette.custom.gray[200]}`,
        borderRadius: '4px',
      }}>
        { renderContent() }
      </Box>
      <PodConfigurationInfo
        open={isPodInfoModalOpen}
        onClose={() => setIsPodInfoModalOpen(false)}
      />
      <MessagesSnackbar
        open={showSuccessMessage}
        onClose={() => setShowSuccessMessage(false)}
        message={t('successfullyUpdated')}
        severity="success"
        duration={3000}
      />
      <MessagesSnackbar
        open={showErrorMessage}
        onClose={() => setShowErrorMessage(false)}
        message={t('errorUpdating')}
        severity="error"
        duration={null}
      />
    </PageArea>
  );
};

export default ManagePodConfigurations;